import { EditorShell, InspectorGroup, useAutosave, type ValidationItem } from '@components/Common/EditorShell';
import {
  PlaceholderManager,
  parseTokens,
  removeToken,
  renameToken,
} from '@components/Common/PlaceholderManager';
import Editor, { type OnMount } from '@monaco-editor/react';

type CodeEditor = Parameters<OnMount>[0];
type MonacoApi = Parameters<OnMount>[1];
import FormField from '@components/Common/FormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEVICE_WIDTH, UNFILLED_MARK, wrapInLayout, type PreviewDevice } from './emailLayout';
import type { EmailTemplate, EmailTemplateListItem, TemplateVersion } from './types';
import { AUDIENCE_BY_CATEGORY, KEY_PREFIX_BY_AUDIENCE, SUBJECT_TRUNCATION } from './types';
import './editor.scss';
import { formatDateTime } from '@helpers/date';

interface TemplateEditorProps {
  templateKey: string;
  summary: EmailTemplateListItem | null;
  onBack: () => void;
  /** Keeps the parent list in step with what was saved here. */
  onSaved: (patch: Partial<EmailTemplateListItem>) => void;
  /** Automation rules referencing this key, for the usage line. */
  usageRuleCount: number | null;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  templateKey,
  summary,
  onBack,
  onSaved,
  usageRuleCount,
}) => {
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [declared, setDeclared] = useState<string[]>([]);
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({});
  const [isActive, setIsActive] = useState(false);
  const [category, setCategory] = useState('');

  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [previewSrc, setPreviewSrc] = useState('');
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewRef = useRef<HTMLIFrameElement | null>(null);
  const editorRef = useRef<CodeEditor | null>(null);
  const completionRef = useRef<{ dispose: () => void } | null>(null);
  /** Read by the completion provider, which is registered once. */
  const knownTokensRef = useRef<string[]>([]);

  /* ── Load ─────────────────────────────────────────────────────────────── */

  useEffect(() => {
    setLoading(true);
    axios
      .get<EmailTemplate>(`/api/email-templates/${templateKey}`)
      .then(({ data }) => {
        setSubject(data.subject ?? '');
        setPreviewText(data.previewText ?? '');
        setHtmlContent(data.htmlContent ?? '');
        setDeclared(data.availableVariables ?? []);
        setIsActive(!!data.isActive);
        setCategory(data.category ?? '');
        setDirty(false);
      })
      .catch(() => showToast('Failed to load template content', 'error'))
      .finally(() => setLoading(false));
  }, [templateKey, showToast]);

  /* ── Preview ──────────────────────────────────────────────────────────── */

  const renderPreview = useCallback(
    (pt: string, html: string, values: Record<string, string>, dev: PreviewDevice, cat: string) => {
      let resolvedPt = pt;
      let resolvedHtml = html;
      // An unfilled token renders highlighted amber inline rather than as blank
      // space — blank space is indistinguishable from intentional whitespace.
      for (const name of parseTokens(html + ' ' + pt).keys()) {
        const value = values[name];
        const token = `{{${name}}}`;
        resolvedPt = resolvedPt.split(token).join(value || `{{${name}}}`);
        resolvedHtml = resolvedHtml.split(token).join(value || UNFILLED_MARK(name));
      }
      setPreviewSrc(wrapInLayout(resolvedPt, resolvedHtml, cat !== 'AUTH', dev));
    },
    [],
  );

  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(
      () => renderPreview(previewText, htmlContent, sampleValues, device, category),
      400,
    );
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [previewText, htmlContent, sampleValues, device, category, renderPreview]);

  /**
   * Source and preview scroll together — this is the one editor where the two
   * must be read side by side, and hunting for the same block twice is the
   * friction that makes people stop using the preview.
   */
  const syncPreviewScroll = useCallback((top: number, height: number, viewport: number) => {
    const doc = previewRef.current?.contentDocument?.scrollingElement;
    if (!doc) return;
    const range = height - viewport;
    if (range <= 0) return;
    doc.scrollTop = (top / range) * (doc.scrollHeight - doc.clientHeight);
  }, []);

  /* ── Editing ──────────────────────────────────────────────────────────── */

  const updateHtml = useCallback((next: string) => {
    setHtmlContent(next);
    setDirty(true);
  }, []);

  /** Insert at the caret via Monaco's edit API — the chip row and the picker
   *  both land where the author is actually typing. */
  const insertToken = useCallback((name: string) => {
    const ed = editorRef.current;
    if (!ed) return;
    const selection = ed.getSelection();
    if (!selection) return;
    ed.executeEdits('placeholder', [{ range: selection, text: `{{${name}}}`, forceMoveMarkers: true }]);
    ed.focus();
  }, []);

  // Everything the picker may offer. Held in a ref because the completion
  // provider is registered once and must not close over a stale list.
  knownTokensRef.current = useMemo(() => {
    const used = Array.from(parseTokens(`${subject} ${previewText} ${htmlContent}`).keys());
    return Array.from(new Set([...declared, ...used]));
  }, [declared, subject, previewText, htmlContent]);

  /**
   * Registers the "{{" picker on Monaco itself rather than overlaying our own,
   * so it behaves like every other completion in the editor: arrow keys, Enter,
   * Escape, and no fighting over focus.
   */
  const handleEditorMount = useCallback(
    (ed: CodeEditor, monaco: MonacoApi) => {
      editorRef.current = ed;

      completionRef.current?.dispose();
      completionRef.current = monaco.languages.registerCompletionItemProvider('html', {
        triggerCharacters: ['{'],
        provideCompletionItems: (model: any, position: any) => {
          const line = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });
          const open = line.lastIndexOf('{{');
          if (open === -1) return { suggestions: [] };
          const partial = line.slice(open + 2);
          if (partial.includes('}') || /[^a-zA-Z0-9_]/.test(partial)) return { suggestions: [] };

          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: open + 1,
            endColumn: position.column,
          };
          return {
            suggestions: knownTokensRef.current.map((name) => ({
              label: name,
              kind: monaco.languages.CompletionItemKind.Variable,
              detail: 'placeholder',
              insertText: `{{${name}}}`,
              range,
            })),
          };
        },
      });

      // Keep the preview aligned with wherever the author is reading.
      ed.onDidScrollChange((e: { scrollTop: number; scrollHeight: number }) => {
        syncPreviewScroll(e.scrollTop, e.scrollHeight, ed.getLayoutInfo().height);
      });
    },
    [syncPreviewScroll],
  );

  // Monaco's provider is global to the 'html' language, so it must be released
  // when the editor unmounts or a second one would double the suggestions.
  useEffect(() => () => completionRef.current?.dispose(), []);

  const handleRename = (from: string, to: string) => {
    setHtmlContent((p) => renameToken(p, from, to));
    setSubject((p) => renameToken(p, from, to));
    setPreviewText((p) => renameToken(p, from, to));
    setDeclared((p) => p.map((d) => (d === from ? to : d)));
    setSampleValues((p) => {
      const { [from]: value, ...rest } = p;
      return value === undefined ? rest : { ...rest, [to]: value };
    });
    setDirty(true);
  };

  const handleRemove = (name: string) => {
    setHtmlContent((p) => removeToken(p, name));
    setSubject((p) => removeToken(p, name));
    setPreviewText((p) => removeToken(p, name));
    setDeclared((p) => p.filter((d) => d !== name));
    setDirty(true);
  };

  /* ── Validation ───────────────────────────────────────────────────────── */

  const audience = AUDIENCE_BY_CATEGORY[category] ?? null;
  const expectedPrefix = audience ? KEY_PREFIX_BY_AUDIENCE[audience] : null;

  const validation = useMemo<ValidationItem[]>(() => {
    const items: ValidationItem[] = [];
    // Subject and preheader carry tokens too, and an undeclared one there is
    // just as broken as in the body.
    const used = parseTokens(`${subject} ${previewText} ${htmlContent}`);
    const undeclared = Array.from(used.keys()).filter((t) => !declared.includes(t));
    const unused = declared.filter((d) => !used.has(d));
    const noSample = declared.filter((d) => used.has(d) && !sampleValues[d]);

    if (!subject.trim()) {
      items.push({ id: 'subject', level: 'blocker', message: 'Subject is empty.' });
    }
    if (expectedPrefix && !templateKey.startsWith(expectedPrefix)) {
      items.push({
        id: 'prefix',
        level: 'blocker',
        message: `Key "${templateKey}" does not start with "${expectedPrefix}", which this ${audience} template requires.`,
      });
    }
    if (undeclared.length) {
      items.push({
        id: 'undeclared',
        level: 'blocker',
        message: `${undeclared.length} placeholder${undeclared.length === 1 ? '' : 's'} used but never declared (${undeclared.join(', ')}). They would send empty.`,
        fixLabel: 'Declare them',
        onFix: () => { setDeclared((p) => [...p, ...undeclared]); setDirty(true); },
      });
    }
    if (subject.length > SUBJECT_TRUNCATION) {
      items.push({
        id: 'subject-length',
        level: 'warning',
        message: `Subject is ${subject.length} characters; most inboxes truncate around ${SUBJECT_TRUNCATION}.`,
      });
    }
    if (noSample.length) {
      items.push({
        id: 'no-sample',
        level: 'warning',
        message: `${noSample.length} placeholder${noSample.length === 1 ? ' has' : 's have'} no sample value, so the preview cannot show ${noSample.length === 1 ? 'it' : 'them'} filled.`,
      });
    }
    if (unused.length) {
      items.push({
        id: 'unused',
        level: 'warning',
        message: `${unused.length} declared placeholder${unused.length === 1 ? ' is' : 's are'} not used anywhere.`,
        fixLabel: 'Remove them',
        onFix: () => { setDeclared((p) => p.filter((d) => !unused.includes(d))); setDirty(true); },
      });
    }
    return items;
  }, [subject, previewText, htmlContent, declared, sampleValues, templateKey, expectedPrefix, audience]);

  /* ── Save / publish ───────────────────────────────────────────────────── */

  const persist = useCallback(async () => {
    await axios.put(`/api/email-templates/${templateKey}`, { subject, previewText, htmlContent });
    setDirty(false);
    onSaved({ subject, previewText });
  }, [templateKey, subject, previewText, htmlContent, onSaved]);

  const { state: saveState, savedAt } = useAutosave({
    dirty,
    // Drafts only: an ACTIVE template is being sent to subscribers right now.
    enabled: !isActive && !!subject.trim(),
    onSave: persist,
  });

  const handleToggleActive = async () => {
    setPublishing(true);
    try {
      if (dirty) await persist();
      const { data } = await axios.patch<{ isActive: boolean }>(
        `/api/email-templates/${templateKey}/toggle`,
      );
      setIsActive(data.isActive);
      onSaved({ isActive: data.isActive });
      showToast(data.isActive ? 'Template is live' : 'Template paused', 'success');
    } catch {
      showToast('Failed to change template status', 'error');
    } finally {
      setPublishing(false);
    }
  };

  /* ── Versions ─────────────────────────────────────────────────────────── */

  const openVersions = async () => {
    setVersionsOpen(true);
    setVersionsLoading(true);
    try {
      const { data } = await axios.get<TemplateVersion[]>(
        `/api/email-templates/${templateKey}/versions`,
      );
      setVersions(data);
    } catch {
      showToast('Failed to load version history', 'error');
    } finally {
      setVersionsLoading(false);
    }
  };

  /**
   * Restore LOADS A DRAFT. It never writes.
   *
   * The server's POST /restore/:index persists immediately, which means one
   * click puts an old template back in front of every subscriber. The version
   * payload already carries the full content, so the editor loads it locally
   * and the author saves — or does not — with their eyes open.
   */
  const loadVersionAsDraft = (v: TemplateVersion) => {
    setSubject(v.subject ?? '');
    setPreviewText(v.previewText ?? '');
    setHtmlContent(v.htmlContent ?? '');
    setDirty(true);
    setVersionsOpen(false);
    showToast('Version loaded as an unsaved draft. Review, then save', 'info');
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      await axios.post(`/api/email-templates/${templateKey}/send-test`, { to: testEmail });
      showToast(`Test sent to ${testEmail}`, 'success');
      setTestOpen(false);
    } catch {
      showToast('Failed to send the test email', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) return <SkeletonLoader />;

  /* ── Inspector ────────────────────────────────────────────────────────── */

  const inspector = (
    <>
      {/* Usage before the author touches anything: editing a template that
          feeds live automation is a different decision from editing a draft. */}
      {usageRuleCount !== null && (
        <InspectorGroup title="Usage" icon={<InsightsOutlinedIcon />}>
          <Typography className="cc-ed__hint">
            {usageRuleCount === 0
              ? 'No automation rule references this template.'
              : `Used by ${usageRuleCount} automation rule${usageRuleCount === 1 ? '' : 's'}.`}
          </Typography>
        </InspectorGroup>
      )}

      <InspectorGroup title="Identity" icon={<BadgeOutlinedIcon />}>
        <FormField
          required={false}
          label="Template key"
          name="templateKey"
          value={templateKey}
          disabled
          onChange={() => {}}
          helperText="Automation rules and drip campaigns reference this key."
        />
        <FormField
          required
          label="Subject"
          name="subject"
          value={subject}
          onChange={(_, v) => { setSubject(String(v ?? '')); setDirty(true); }}
          helperText={`${subject.length}/${SUBJECT_TRUNCATION} before most inboxes truncate. Supports {{variables}}.`}
        />
        <FormField
          required={false}
          label="Preheader"
          name="previewText"
          value={previewText}
          onChange={(_, v) => { setPreviewText(String(v ?? '')); setDirty(true); }}
          helperText="Shown next to the subject in the inbox, where they are read together."
        />
      </InspectorGroup>

      {/* Subject and preheader as the inbox shows them, side by side. */}
      <Box className="et-inbox-preview">
        <Box className="et-inbox-preview__label">Inbox preview</Box>
        <Box className="et-inbox-preview__subject">
          {subject.slice(0, SUBJECT_TRUNCATION) || 'Subject'}
          {subject.length > SUBJECT_TRUNCATION && <span className="et-inbox-preview__cut">…</span>}
        </Box>
        <Box className="et-inbox-preview__pre">{previewText || 'Preheader text'}</Box>
      </Box>

      <InspectorGroup title="Classification" icon={<LabelOutlinedIcon />}>
        <FormField required={false} label="Category" name="category" value={category} disabled onChange={() => {}} />
        <FormField
          required={false}
          label="Audience"
          name="audience"
          value={audience ?? 'Not audience-specific'}
          disabled
          onChange={() => {}}
          helperText={expectedPrefix ? `Keys must start with "${expectedPrefix}".` : undefined}
        />
      </InspectorGroup>

      <InspectorGroup
        title="Placeholders"
        icon={<DataObjectOutlinedIcon />}
        hint="Type {{ in the HTML to insert one. Subject and preheader are checked too."
      >
        <PlaceholderManager
          body={`${subject} ${previewText} ${htmlContent}`}
          declared={declared}
          autoVars={[]}
          sampleValues={sampleValues}
          onDeclaredChange={(next) => { setDeclared(next); setDirty(true); }}
          onSampleValueChange={(name, value) =>
            setSampleValues((p) => ({ ...p, [name]: value }))
          }
          onRename={handleRename}
          onRemove={handleRemove}
          onInsert={insertToken}
        />
      </InspectorGroup>

      <InspectorGroup title="Versions" icon={<HistoryOutlinedIcon />}>
        <Button size="small" variant="outlined" onClick={openVersions} fullWidth>
          Version history
        </Button>
        <Typography className="cc-ed__hint">
          Restoring loads the version as an unsaved draft. It never publishes.
        </Typography>
      </InspectorGroup>
    </>
  );

  /* ── Canvas: split source / preview ───────────────────────────────────── */

  return (
    <>
      <EditorShell
        backLabel="Templates"
        onBack={onBack}
        name={summary?.name ?? templateKey}
        status={isActive ? 'Live' : 'Paused'}
        statusTone={isActive ? 'var(--green)' : 'var(--grey)'}
        statusTint={isActive ? 'var(--green-tint)' : 'var(--grey-tint)'}
        saveState={saveState}
        savedAt={savedAt}
        onPreview={() => setDevice((d) => (d === 'desktop' ? 'mobile' : 'desktop'))}
        previewLabel={device === 'desktop' ? 'Mobile 375px' : 'Desktop 600px'}
        secondaryAction={{ label: 'Send test', onClick: () => setTestOpen(true) }}
        stateAction={{
          label: isActive ? 'Pause' : 'Publish',
          onClick: handleToggleActive,
          busy: publishing,
        }}
        validation={validation}
        inspector={inspector}
        redirectTitle="Email template editor"
        redirectReason="Source and rendered preview must be visible together, which needs a desktop-width canvas."
      >
        <Box className="et-split">
          <Box className="et-split__pane">
            <Box className="et-split__label">HTML source</Box>
            <Box className="et-source">
              <Editor
                height="100%"
                language="html"
                theme="vs-dark"
                value={htmlContent}
                onChange={(v) => updateHtml(v ?? '')}
                onMount={handleEditorMount}
                options={{
                  minimap: { enabled: false },
                  wordWrap: 'on',
                  fontSize: 'var(--font-size-2xs)',
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2,
                  // The picker is the entry path for tokens, so it must appear
                  // as soon as "{{" is typed rather than on a keyboard shortcut.
                  quickSuggestions: { other: true, comments: false, strings: true },
                }}
              />
            </Box>
          </Box>
          <Box className="et-split__pane">
            <Box className="et-split__label">
              Preview · {device === 'desktop' ? '600px' : '375px'}
            </Box>
            <Box className="et-preview-frame">
              <iframe
                ref={previewRef}
                className="et-preview-iframe"
                srcDoc={previewSrc}
                title="Email preview"
                style={{ width: DEVICE_WIDTH[device], maxWidth: '100%' }}
              />
            </Box>
          </Box>
        </Box>
      </EditorShell>

      {/* ── Versions ── */}
      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Version history</DialogTitle>
        <DialogContent dividers>
          {versionsLoading && <SkeletonLoader />}
          {!versionsLoading && versions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No earlier versions recorded.
            </Typography>
          )}
          {!versionsLoading &&
            versions.map((v, i) => (
              <Box key={i} className="et-version-row">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography className="et-version-subject">{v.subject || '(no subject)'}</Typography>
                  <Typography className="et-version-meta">
                    {formatDateTime(v.savedAt, 'Unknown time')}
                    {v.savedBy ? ` · ${v.savedBy}` : ''}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => loadVersionAsDraft(v)}>
                  Load as draft
                </Button>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Send test ── */}
      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send a test</DialogTitle>
        <DialogContent>
          <FormField
            required={false}
            label="Send to"
            name="testEmail"
            type="email"
            value={testEmail}
            onChange={(_, v) => setTestEmail(String(v ?? ''))}
            helperText="Uses the current unsaved content."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendTest} disabled={!testEmail || sendingTest}>
            {sendingTest ? 'Sending…' : 'Send test'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateEditor;
